/**
 * StepDetailsModal
 *
 * Interim canonical dialogue for treatment-hub steps whose data used to render
 * as a bespoke inline form in the step card (fertilization, embryo development,
 * freezing, screening, PGT). The step shows a canonical "Add … details" row
 * that opens this dialogue, which renders the SAME inputs using the app's
 * canonical dialogue components — icon-labelled fields (`MoonFormField`), the
 * canonical inputs, and a velvet `DialogPrimaryButton` — so it matches every
 * other Moon dialogue. Proper per-step redesign is tracked in #2094–#2100.
 */

import { useEffect, useState } from "react";
import { ScrollView, TextInput, View, useWindowDimensions } from "react-native";
import {
  Dialog,
  DialogHeader,
  DialogIcon,
  DialogPrimaryButton,
  MoonFormField,
  MoonFieldInput,
  MoonDateField,
  InlineOptionPicker,
  colors,
} from "@louise/moon-ui";
import {
  CalendarDays,
  Dna,
  Egg,
  FileText,
  FlaskConical,
  Hash,
  ListChecks,
  Snowflake,
  Sparkles,
  StickyNote,
  UserRound,
  type LucideIcon,
} from "lucide-react-native";
import type { TreatmentStepProgress } from "@louise/care/treatment-phases";
import type { TreatmentStepConfig } from "@src/packages/homepage/sections/TreatmentProgressModal";
import { TreatmentUploadField } from "@src/packages/homepage/sections/TreatmentUploadField";
import { useTranslations } from "@src/packages/i18n";
import { MoonDateTimeFieldPicker } from "./MoonDateTimeFieldPicker";
import { getStepIllustration } from "./stepIllustration";

type StepData = Record<string, string | number | boolean | null>;
type StepField = NonNullable<TreatmentStepConfig["fields"]>[number];

interface StepDetailsModalProps {
  visible: boolean;
  onClose: () => void;
  step: TreatmentStepConfig;
  progress: TreatmentStepProgress | null;
  onComplete: (data: StepData) => void;
}

// Meaningful label icons per known field; falls back to a type-based icon.
const FIELD_ICONS: Record<string, LucideIcon> = {
  spermSource: UserRound,
  spermType: Snowflake,
  fertilizationMethod: FlaskConical,
  eggsFertilized: Egg,
  eggsCollected: Egg,
  eggsFrozen: Snowflake,
  vialsFrozen: Snowflake,
  embryosFrozen: Snowflake,
  embryoGrade: Sparkles,
  topEmbryoGrade: Sparkles,
  euploidCount: Dna,
};

function fieldIcon(field: StepField): LucideIcon {
  const known = FIELD_ICONS[field.name];
  if (known) return known;
  const name = field.name.toLowerCase();
  if (name.includes("date")) return CalendarDays;
  if (name.includes("note")) return StickyNote;
  switch (field.type) {
    case "number":
      return Hash;
    case "select":
    case "dropdown":
      return ListChecks;
    case "date":
    case "datetime":
      return CalendarDays;
    case "upload":
      return FileText;
    case "textarea":
      return StickyNote;
    default:
      return FileText;
  }
}

// Medical acronyms that should read fully uppercase as option labels.
const ACRONYMS = new Set(["ivf", "icsi", "iui", "std", "pgt", "hcg"]);

// "yes" → "Yes", "fresh" → "Fresh", "ivf" → "IVF".
function optionLabel(option: string): string {
  if (ACRONYMS.has(option.toLowerCase())) return option.toUpperCase();
  return option.replace(/\b[a-z]/g, (c) => c.toUpperCase());
}

export function StepDetailsModal({
  visible,
  onClose,
  step,
  progress,
  onComplete,
}: StepDetailsModalProps) {
  const t = useTranslations();
  const { height } = useWindowDimensions();
  const dialogMaxHeight = Math.min(820, Math.max(520, height * 0.9));
  const illustration = getStepIllustration(step.id);
  const [data, setData] = useState<StepData>(() => ({
    ...(progress?.data ?? {}),
  }));

  // Re-seed from the saved values whenever the dialogue is (re)opened.
  useEffect(() => {
    if (visible) setData({ ...(progress?.data ?? {}) });
  }, [visible, step.id, progress]);

  const setField = (name: string, value: string | number | null) =>
    setData((prev) => ({ ...prev, [name]: value }));

  const visibleFields = (step.fields ?? []).filter(
    (field) =>
      !field.showIf || data[field.showIf.field] === field.showIf.equals,
  );
  const missingRequired = visibleFields.find((field) => {
    if (!field.required) return false;
    const value = data[field.name];
    return value === undefined || value === null || value === "";
  });
  const canSave = !missingRequired;

  const handleSave = () => {
    if (!canSave) return;
    onComplete(data);
    onClose();
  };

  const renderField = (field: StepField) => {
    const Icon = fieldIcon(field);
    const raw = data[field.name];

    if (field.type === "select" || field.type === "dropdown") {
      const options = (field.options ?? []).map((option) => ({
        id: option,
        label: optionLabel(option),
      }));
      return (
        <MoonFormField
          key={field.name}
          label={field.label}
          labelIcon={Icon}
          required={field.required}
        >
          <InlineOptionPicker
            options={options}
            value={typeof raw === "string" ? raw : ""}
            onChange={(value) => setField(field.name, value)}
            wrap={options.length > 3}
            columns={2}
          />
        </MoonFormField>
      );
    }

    if (field.type === "date") {
      const dateValue = typeof raw === "string" && raw ? raw.slice(0, 10) : "";
      return (
        <MoonDateField
          key={field.name}
          label={field.label}
          labelIcon={Icon}
          required={field.required}
          value={dateValue}
          onChange={(date) => setField(field.name, date)}
          placeholder={field.placeholder}
        />
      );
    }

    if (field.type === "datetime") {
      return (
        <MoonFormField
          key={field.name}
          label={field.label}
          labelIcon={Icon}
          required={field.required}
        >
          <MoonDateTimeFieldPicker
            value={typeof raw === "string" ? raw : null}
            onChange={(iso) => setField(field.name, iso)}
            placeholder={field.placeholder}
          />
        </MoonFormField>
      );
    }

    if (field.type === "upload") {
      return (
        <MoonFormField
          key={field.name}
          label={field.label}
          labelIcon={Icon}
          required={field.required}
        >
          <TreatmentUploadField
            value={typeof raw === "string" ? raw : null}
            onChange={(docId, uploadId) => {
              setField(field.name, docId);
              if (step.id === "pgt_testing") {
                setField("pgtResultUploadId", uploadId);
              }
            }}
            tags={["medical_records", `step:${step.id}`]}
          />
        </MoonFormField>
      );
    }

    const multiline = field.type === "textarea";
    const numeric = field.type === "number";
    return (
      <MoonFormField
        key={field.name}
        label={field.label}
        labelIcon={Icon}
        required={field.required}
      >
        <MoonFieldInput paddingY={multiline ? 10 : 0}>
          <TextInput
            value={raw == null ? "" : String(raw)}
            onChangeText={(text) =>
              setField(
                field.name,
                numeric
                  ? text === ""
                    ? null
                    : Number(text.replace(/[^0-9.]/g, ""))
                  : text,
              )
            }
            placeholder={field.placeholder}
            placeholderTextColor={colors.textFaint}
            keyboardType={numeric ? "number-pad" : "default"}
            multiline={multiline}
            style={{
              fontSize: 14,
              color: colors.text,
              minHeight: multiline ? 88 : 44,
              textAlignVertical: multiline ? "top" : "center",
              paddingVertical: multiline ? 0 : 10,
            }}
          />
        </MoonFieldInput>
      </MoonFormField>
    );
  };

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      closeButton="minimal"
      containerStyle={{ maxHeight: dialogMaxHeight }}
      surfaceStyle={{ maxHeight: dialogMaxHeight }}
      contentStyle={{
        paddingTop: 20,
        paddingHorizontal: 20,
        paddingBottom: 18,
        maxHeight: dialogMaxHeight,
      }}
    >
      <View style={{ flex: 1, minHeight: 0 }}>
        {illustration ? (
          <DialogIcon
            source={illustration.source}
            width={72}
            height={64}
            style={{ marginBottom: 4 }}
          />
        ) : null}
        <DialogHeader
          title={step.title}
          subtitle={step.prompt ?? step.description}
          style={{ marginBottom: 14 }}
          titleStyle={{ textAlign: "center", width: "100%" }}
          subtitleStyle={{ textAlign: "center", width: "100%" }}
        />
        <ScrollView
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          style={{ flexShrink: 1 }}
          contentContainerStyle={{ gap: 12, paddingBottom: 4 }}
        >
          {visibleFields.map(renderField)}
        </ScrollView>
        <View
          style={{
            marginTop: 14,
            paddingTop: 14,
            borderTopWidth: 1,
            borderTopColor: "rgba(42,37,34,0.08)",
          }}
        >
          <DialogPrimaryButton
            label={t("care.treatment_flows.actions.confirm_save")}
            onPress={handleSave}
            disabled={!canSave}
            fullWidth
          />
        </View>
      </View>
    </Dialog>
  );
}
